'use strict';

/**
 * Apple Pay session guard (LEG-562).
 *
 * The platform Apple Pay script (internal/jscript/applepay.js) creates a new
 * ApplePaySession on every tap of the Apple Pay button and moves its event
 * handlers onto the new session BEFORE calling begin(). When WebKit still holds
 * the previous session, begin() throws "Page already has an active payment
 * session." and the live session is left with no handlers, so the sheet dies with
 * nothing shown to the shopper. Ending the stale session first lets the new
 * begin() succeed with the handlers already in the right place.
 *
 * WebKit frees the page's payment session synchronously inside abort(), so the
 * new begin() sees no active session. It can still be refused by the UI process
 * while the previous sheet is dismissing, which throws a different error; that
 * case shows the message instead of leaving the tap silent.
 *
 * The platform keeps ONE module-level session reference and its failure paths
 * abort whatever that reference currently holds. Once a sheet is authorized the
 * order already exists and the card is already authorized server-side, so ending
 * it there would strand a paid order. Those aborts are refused. The navigation
 * the platform runs after such an abort is disarmed on the server side: the
 * client hook overrides return a null redirect, so doRedirect() has nothing to do.
 */
module.exports = function () {
    try {
        var Session = window.ApplePaySession;
        if (!Session || !Session.prototype || Session.prototype.lwSessionGuard) {
            return;
        }

        var nativeBegin = Session.prototype.begin;
        var nativeAbort = Session.prototype.abort;
        var nativeComplete = Session.prototype.completePayment;

        var current = null;        // session whose begin() we last saw succeed; may have ended since
        var authorized = false;    // that session has an authorization in flight
        var guardAborting = false; // this module, not the platform, called abort()

        // Presentation only. It must never throw into the payment path.
        function message(show) {
            try {
                var box = document.querySelector('.lw-applepay-message');
                if (!box) {
                    return;
                }
                box.textContent = show ? box.getAttribute('data-message') : '';
                // The text lands while the system sheet is tearing down, when the page does
                // not hold accessibility focus, so a live region alone is unlikely to be
                // spoken. Moving focus onto the message reads it out and scrolls it into
                // view. Not while the shopper is typing somewhere (a field, or a payment
                // iframe): a late failure must not pull them out of what they are entering.
                var active = document.activeElement;
                var typing = !!(active && /^(input|textarea|select|iframe)$/i.test(active.tagName || ''));
                if (show && !typing) {
                    box.focus();
                }
            } catch (e) {
                // Nothing here is worth breaking an Apple Pay sheet for.
            }
        }

        Session.prototype.begin = function () {
            // Only act on a real session. A prototype method invoked with a foreign receiver
            // must not be able to end the shopper's live sheet.
            if (!(this instanceof Session)) {
                return nativeBegin.apply(this, arguments);
            }
            // The shopper is trying again: clear whatever the last attempt said, so any message
            // that follows is a fresh empty-to-text change a screen reader will announce.
            message(false);
            // Did we take the page away from a previous session? True whether our abort
            // succeeded or threw, because in both cases that sheet is gone. It is FALSE when
            // a live sheet still exists (an authorization in flight), which is the case where
            // telling the shopper the payment failed would be both wrong and alarming.
            var tookOver = false;
            if (current && current !== this && !authorized) {
                tookOver = true;
                guardAborting = true;
                try {
                    current.abort();
                } catch (e) {
                    // Previous session already ended (cancelled, completed or aborted).
                } finally {
                    guardAborting = false;
                }
            }

            var result;
            try {
                result = nativeBegin.apply(this, arguments);
            } catch (e) {
                // WebKit refused a new sheet. The shopper had one a moment ago and now has
                // none, whether the guard ended it or it had already died, so say so rather
                // than leaving the tap silent.
                if (tookOver) {
                    message(true);
                }
                throw e;
            }

            current = this;
            authorized = false;
            this.addEventListener('paymentauthorized', function () {
                if (current === this) { authorized = true; }
            });
            return result;
        };

        Session.prototype.abort = function () {
            // The platform aborts whatever session its module-level variable holds,
            // which after a re-tap is the LIVE sheet. Once that sheet is authorized
            // the order exists and the card is authorized, and the only remaining
            // step is the form post the platform makes after completePayment. Ending
            // it here would strand a paid order, so refuse.
            if (!guardAborting && this === current && authorized) {
                return undefined;
            }
            var result = nativeAbort.apply(this, arguments);
            // `current` keeps pointing at the ended session on purpose: the next begin()
            // then knows a sheet was taken away, so a refused re-tap is not silent.
            // The guard's own abort is a repair, not a failure: say nothing.
            if (!guardAborting) {
                message(true);
            }
            return result;
        };

        Session.prototype.completePayment = function () {
            if (!(this instanceof Session)) {
                return nativeComplete.apply(this, arguments);
            }
            try {
                return nativeComplete.apply(this, arguments);
            } finally {
                // The authorization attempt has concluded either way. WebKit returns
                // the session to Active for the non-final statuses, so the exemption
                // must not outlive the attempt.
                authorized = false;
                // Any status but success means this attempt did not go through: show the page's
                // sentence (try again or pay another way); a success clears it. A late reply from
                // an earlier sheet lands on the newer one, where WebKit may refuse the call; that
                // attempt still failed. Version 1 passes a number; later ones may pass an object.
                var result = arguments[0];
                var status = (result && result.status !== undefined) ? result.status : result;
                if (status !== Session.STATUS_SUCCESS) {
                    message(true);
                } else {
                    message(false);
                }
            }
        };

        Session.prototype.lwSessionGuard = true;

        // Checkout changes stages on the same document. Once the shopper moves to another
        // stage, the sentence describes an attempt they already left behind: clear it so it
        // is neither re-read as a live alert nor shown where the button it names is hidden.
        // Checkout also rewrites the attribute with the SAME value (a failed Continue), and
        // then the shopper is still where the button is: keep the sentence.
        var stage = document.querySelector('.data-checkout-stage');
        if (stage && typeof MutationObserver === 'function') {
            new MutationObserver(function (records) {
                for (var i = 0; i < records.length; i++) {
                    if (records[i].oldValue !== stage.getAttribute('data-checkout-stage')) {
                        message(false);
                        return;
                    }
                }
            }).observe(stage, { attributes: true, attributeOldValue: true, attributeFilter: ['data-checkout-stage'] });
        }
    } catch (e) {
        // The guard must never be the reason the rest of the page fails to start.
    }
};
